import { get } from 'lodash';
import {
  OKTA_LOGOUT_URL, SESSION_STORAGE_ITEMS, LOCAL_STORAGE_ITEMS, PORTAL_ACCESS,
} from '../constants/AppConstants';
import { logError } from './logger.utils';

/**
 * Sets the token in session storage with the access token received
 * @param {Object} token - access token
 */
export const setAuthToken = (token) => {
  sessionStorage.setItem('token', JSON.stringify(token));
};

/**
 * Gets the access token from the session storage
 */
export const getAuthToken = () => {
  const token = JSON.parse(sessionStorage.getItem('token'));
  return get(token, 'access_token', '');
};

/**
 * Checks and gets the access token if it is present the URL.
 * After storing it in sessionStorage, removes access_token from the address bar
 * so it is not retained in history or leaked via Referer.
 */
export const getTokenFromUrl = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const token = urlSearchParams.get('access_token');
  if (token) {
    setAuthToken({ access_token: token });
    urlSearchParams.delete('access_token');
    const remaining = urlSearchParams.toString();
    const search = remaining ? `?${remaining}` : '';
    const cleanedUrl = `${window.location.pathname}${search}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', cleanedUrl);
  }
  return getAuthToken();
};

/**
 * Checks if the user is entitled based on entitlements object
 */
export const isEntitled = (entitlements) => {
  // #genai optional chaining, same truthiness guard as before
  if (entitlements?.myev) return entitlements.myev.includes(PORTAL_ACCESS);
  return false;
};

export const isEntitledForIngredient = (entitlements, ingredient) => {
  // #genai - optional chaining, same truthiness guard as before
  if (entitlements?.myev) return entitlements.myev.includes(ingredient);
  return false;
};

export const isEntitledForEvOneIngredient = (entitlements, ingredient) => {
  // #genai - optional chaining, same truthiness guard as before
  if (entitlements?.property) return entitlements.property.includes(ingredient);
  return false;
};

/**
 * Checks if the user is authorized based on the access token present in the URL or session storage
 */
export const isAuthorized = () => (getTokenFromUrl() !== '') || !!sessionStorage.getItem('token');

export const getAuthIdToken = () => {
  const token = JSON.parse(localStorage.getItem('token'));
  // #genai optional chaining, empty string fallback is unchanged for missing tokens
  const authIdToken = token?.id_token || '';
  return authIdToken;
};

export const signOutUser = () => {
  const idToken = getAuthIdToken();
  const redirectUri = `${window.location.origin}`;
  const urlsearch = new URLSearchParams(`id_token_hint=${idToken}&post_logout_redirect_uri=${redirectUri}`);
  const logoutUrl = `${OKTA_LOGOUT_URL}?${urlsearch.toString()}`;
  SESSION_STORAGE_ITEMS.forEach((item) => sessionStorage.removeItem(item));
  LOCAL_STORAGE_ITEMS.forEach((item) => localStorage.removeItem(item));
  window.location.assign(logoutUrl);
};

/**
 * Parses a JWT token and returns the decoded claims
 * @param {string} token - JWT token to parse
 * @returns {Object|null} - Decoded claims object or null if invalid
 */
export const parseJwtToken = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => {
          const hex = c.charCodeAt(0).toString(16).padStart(2, '0');
          return `%${hex}`;
        })
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    logError('Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Gets claims from the current access token
 * @returns {Object|null} - Decoded claims object or null if no valid token
 */
export const getTokenClaims = () => {
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  return parseJwtToken(token);
};
